import classNames from 'classnames'
import React, { useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'

import { useUser } from '../../contexts/UserContext'
import Button from '../Button'
import ChatInput from './ChatInput'
import ConversationManager from './ConversationManager'
import { ChatMessage } from './types'
import { useConversations } from './useConversations'

interface Props {
  className?: string
}

interface ChatTools {
  generete_chart: boolean
  analisar_transcripts: boolean
  sell_sides: boolean
}

const MessageBubble: React.FC<{
  message: ChatMessage
  onRetry?: () => void
  isLastAiMessage?: boolean
  isRetrying?: boolean
  onImageClick?: (imageSrc: string, imageAlt?: string) => void
}> = ({ message, onRetry, isLastAiMessage, isRetrying, onImageClick }) => {
  const isUser = message.who === 'user'

  // Function to check if a URL is an image
  const isImageUrl = (url: string) => {
    // Check file extensions
    if (
      url.endsWith('.png') ||
      url.endsWith('.jpg') ||
      url.endsWith('.jpeg') ||
      url.endsWith('.gif') ||
      url.endsWith('.webp') ||
      url.endsWith('.svg')
    ) {
      return true
    }

    // Check container-file endpoints (these are typically images)
    if (url.includes('/container-file/')) {
      return true
    }

    // Check S3 URLs (assume they are images if they have image-related paths)
    if (
      url.includes('.s3.amazonaws.com/') &&
      (url.includes('/image/') ||
        url.includes('/chart/') ||
        url.includes('/graph/'))
    ) {
      return true
    }

    return false
  }

  // Function to render message content with full Markdown support
  const renderMessageContent = (
    content: string,
    isUserMessage: boolean = false,
    onImageClick?: (imageSrc: string, imageAlt?: string) => void
  ) => {
    const codeStyle = isUserMessage
      ? 'rounded bg-white bg-opacity-20 px-1 py-0.5 text-xs font-mono'
      : 'rounded bg-gray-200 px-1 py-0.5 text-xs font-mono text-gray-800'

    const blockquoteStyle = isUserMessage
      ? 'my-2 border-l-4 border-white border-opacity-50 pl-4 italic opacity-90'
      : 'my-2 border-l-4 border-gray-300 pl-4 italic text-gray-600'

    const linkStyle = isUserMessage
      ? 'text-blue-200 underline hover:text-blue-100'
      : 'text-blue-600 underline hover:text-blue-800'

    return (
      <ReactMarkdown
        className="markdown-content"
        components={{
          // Custom styling for different elements
          p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
          strong: ({ children }) => (
            <strong className="font-semibold">{children}</strong>
          ),
          em: ({ children }) => <em className="italic">{children}</em>,
          code: ({ children }) => <code className={codeStyle}>{children}</code>,
          pre: ({ children }) => (
            <pre className="my-2 overflow-x-auto rounded bg-gray-800 p-3 text-sm text-white">
              {children}
            </pre>
          ),
          ul: ({ children }) => (
            <ul className="mb-2 ml-4 list-disc">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="mb-2 ml-4 list-decimal">{children}</ol>
          ),
          li: ({ children }) => <li className="mb-1">{children}</li>,
          blockquote: ({ children }) => (
            <blockquote className={blockquoteStyle}>{children}</blockquote>
          ),
          h1: ({ children }) => (
            <h1 className="mb-2 text-lg font-bold">{children}</h1>
          ),
          h2: ({ children }) => (
            <h2 className="mb-2 text-base font-bold">{children}</h2>
          ),
          h3: ({ children }) => (
            <h3 className="mb-2 text-sm font-bold">{children}</h3>
          ),
          img: ({ src, alt }) => (
            <img
              src={src}
              alt={alt || 'Image'}
              className="my-2 h-auto max-w-full cursor-pointer rounded border transition-opacity hover:opacity-80"
              onClick={() => onImageClick && onImageClick(src || '', alt)}
              onError={(e) => {
                e.currentTarget.style.display = 'none'
              }}
            />
          ),
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className={linkStyle}
            >
              {children}
            </a>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    )
  }

  return (
    <div
      className={classNames(
        'mb-4 flex',
        isUser ? 'justify-end' : 'justify-start'
      )}
    >
      <div
        className={classNames(
          'max-w-xs rounded-lg px-4 py-2 lg:max-w-md',
          isUser
            ? 'bg-info-dark text-white'
            : 'border border-gray-200 bg-gray-100 text-gray-800'
        )}
      >
        <div className="text-sm">
          {renderMessageContent(message.message, isUser, onImageClick)}
        </div>

        {/* Show streaming indicator for AI messages */}
        {message.who === 'AI' && message.isStreaming && (
          <div className="mt-1 flex items-center space-x-1">
            <div className="h-1 w-1 animate-pulse rounded-full bg-gray-400"></div>
            <div
              className="h-1 w-1 animate-pulse rounded-full bg-gray-400"
              style={{ animationDelay: '0.2s' }}
            ></div>
            <div
              className="h-1 w-1 animate-pulse rounded-full bg-gray-400"
              style={{ animationDelay: '0.4s' }}
            ></div>
          </div>
        )}

        {/* Display files/images if present */}
        {!message.isStreaming && message.files && message.files.length > 0 && (
          <div className="mt-2 space-y-2">
            {message.files.map((file, index) => (
              <div key={index} className="text-xs">
                {isImageUrl(file) ? (
                  <img
                    src={file}
                    alt={`Chart ${index + 1}`}
                    className="h-auto max-w-full cursor-pointer rounded border transition-opacity hover:opacity-80"
                    onClick={() =>
                      onImageClick && onImageClick(file, `Chart ${index + 1}`)
                    }
                    onError={(e) => {
                      // Handle broken image links - show link as fallback
                      const parent = e.currentTarget.parentElement
                      if (parent) {
                        parent.innerHTML = `<a href="${file}" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline hover:text-blue-800">${file}</a>`
                      }
                    }}
                  />
                ) : (
                  <a
                    href={file}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline hover:text-blue-800"
                  >
                    {file}
                  </a>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Display tools if used by user */}
        {message.tools && (
          <div className="mt-2 flex flex-wrap gap-1 text-xs opacity-75">
            {message.tools.generete_chart && (
              <span className="rounded bg-blue-100 px-2 py-1 text-blue-800">
                🔧 Gerar gráfico
              </span>
            )}
            {message.tools.analisar_transcripts && (
              <span className="rounded bg-purple-100 px-2 py-1 text-purple-800">
                📝 Analisar transcripts
              </span>
            )}
            {message.tools.sell_sides && (
              <span className="rounded bg-green-100 px-2 py-1 text-green-800">
                💼 Sell sides
              </span>
            )}
          </div>
        )}

        {/* Retry button for last AI message */}
        {!isUser && isLastAiMessage && !message.isStreaming && onRetry && (
          <div className="mt-2 flex justify-end">
            <button
              onClick={onRetry}
              disabled={isRetrying}
              className="flex items-center space-x-1 rounded px-2 py-1 text-xs text-gray-600 hover:bg-gray-200 hover:text-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
              title="Tentar novamente"
            >
              <svg
                className={classNames(
                  'h-3 w-3',
                  isRetrying ? 'animate-spin' : ''
                )}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              <span>{isRetrying ? 'Tentando...' : 'Tentar novamente'}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

const ToolsSelector: React.FC<{
  tools: ChatTools
  onChange: (tools: ChatTools) => void
}> = ({ tools, onChange }) => {
  return (
    <div className="flex items-center space-x-4 text-sm">
      <span className="text-gray-600">🔧 Tools:</span>
      <label className="flex cursor-pointer items-center space-x-1">
        <input
          type="checkbox"
          checked={tools.generete_chart}
          onChange={(e) =>
            onChange({ ...tools, generete_chart: e.target.checked })
          }
          className="rounded text-info-dark focus:ring-info-dark"
        />
        <span className="text-gray-700">Gerar gráfico</span>
      </label>
      <label className="flex cursor-pointer items-center space-x-1">
        <input
          type="checkbox"
          checked={tools.analisar_transcripts}
          onChange={(e) =>
            onChange({ ...tools, analisar_transcripts: e.target.checked })
          }
          className="rounded text-info-dark focus:ring-info-dark"
        />
        <span className="text-gray-700">Analisar transcripts</span>
      </label>
      <label className="flex cursor-pointer items-center space-x-1">
        <input
          type="checkbox"
          checked={tools.sell_sides}
          onChange={(e) => onChange({ ...tools, sell_sides: e.target.checked })}
          className="rounded text-info-dark focus:ring-info-dark"
        />
        <span className="text-gray-700">Sell sides</span>
      </label>
    </div>
  )
}

const Chat: React.FC<Props> = ({ className }) => {
  const { email } = useUser()

  // Use conversation management hook
  const {
    conversations,
    currentConversationId,
    currentMessages,
    isLoading: conversationsLoading,
    error: conversationsError,
    loadConversation,
    createNewConversation,
    updateCurrentConversationId,
    renameConversation,
    deleteConversation,
    deleteAllConversations,
  } = useConversations(email || undefined)

  const [messages, setMessages] = useState<ChatMessage[]>(currentMessages)
  const [currentMessage, setCurrentMessage] = useState('')
  const [selectedTools, setSelectedTools] = useState<ChatTools>({
    generete_chart: false,
    analisar_transcripts: false,
    sell_sides: false,
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isRetrying, setIsRetrying] = useState(false)
  const [selectedImage, setSelectedImage] = useState<{
    src: string
    alt?: string
  } | null>(null)

  const messagesEndRef = useRef<HTMLDivElement>(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const handleImageClick = (imageSrc: string, imageAlt?: string) => {
    setSelectedImage({ src: imageSrc, alt: imageAlt })
  }

  const handleCloseImageModal = () => {
    setSelectedImage(null)
  }

  // Sync currentMessages from conversation hook with local state
  useEffect(() => {
    setMessages(currentMessages)
  }, [currentMessages])

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const handleSendMessage = async () => {
    if (!currentMessage.trim() || isLoading) return

    const newUserMessage: ChatMessage = {
      who: 'user',
      message: currentMessage,
      tools: selectedTools,
    }

    setMessages((prev) => [...prev, newUserMessage])
    setCurrentMessage('')
    setIsLoading(true)

    // Add empty AI message that will be filled with streaming content
    const streamingMessage: ChatMessage = {
      who: 'AI',
      message: '',
      isStreaming: true,
    }
    setMessages((prev) => [...prev, streamingMessage])

    try {
      // Build messages array in the new format - preserve all message data
      const requestMessages = [...messages, newUserMessage].map((msg) => {
        const message: any = {
          role: msg.who === 'user' ? 'user' : 'assistant',
          content: msg.message,
        }

        // Preserve files if they exist
        if (msg.files && msg.files.length > 0) {
          message.files = msg.files
        }

        // Preserve tools_used if they exist
        if (msg.tools) {
          message.tools_used = msg.tools
        }

        // Preserve metadata if it exists
        if (msg.metadata) {
          message.metadata = msg.metadata
        }

        return message
      })

      // Get token from the existing auth system via a proxy endpoint that can access the httpOnly cookie
      const tokenResponse = await fetch('/api/auth/token', {
        credentials: 'include',
      })

      if (!tokenResponse.ok) {
        throw new Error('Failed to get authentication token')
      }

      const { token } = await tokenResponse.json()

      // Choose endpoint based on selected tools
      const base_address = 'https://api.jgp.com.br' // 'http://localhost:8001'
      const endpoint =
        selectedTools.analisar_transcripts || selectedTools.sell_sides
          ? `${base_address}/compass_llm/api/v1/chat/stream_transcripts`
          : `${base_address}/compass_llm/api/v1/chat/stream`

      // Use the token for direct backend call
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          messages: requestMessages,
          tools: selectedTools,
          user_name: email || 'anonymous',
          conversation_id: currentConversationId,
        }),
      })

      if (!response.ok) {
        throw new Error('Failed to send message')
      }

      // Handle streaming response
      const reader = response.body?.getReader()

      const decoder = new TextDecoder()
      let buffer = '' // Buffer for incomplete data

      if (reader) {
        while (true) {
          const { done, value } = await reader.read()

          if (done) break

          const chunk = decoder.decode(value, { stream: true })
          buffer += chunk

          // Split by double newlines to get complete SSE messages
          const messages = buffer.split('\n\n')
          buffer = messages.pop() || '' // Keep incomplete message in buffer

          for (const message of messages) {
            const lines = message.split('\n')

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const jsonStr = line.slice(6).trim()
                  if (!jsonStr) continue // Skip empty data lines

                  const data = JSON.parse(jsonStr)

                  // Handle text chunks - update streaming message directly
                  if (data.type === 'text_chunk' && data.accumulated) {
                    setMessages((prev) => {
                      const newMessages = [...prev]
                      const lastMessage = newMessages[newMessages.length - 1]
                      if (lastMessage && lastMessage.who === 'AI') {
                        lastMessage.message = data.accumulated
                      }
                      return newMessages
                    })
                  }

                  // Handle completion - final response with complete conversation
                  if (
                    data.type === 'completion' &&
                    data.conversation &&
                    Array.isArray(data.conversation)
                  ) {
                    const newMessages = data.conversation.map((msg: any) => ({
                      who: msg.role === 'user' ? 'user' : 'AI',
                      message: msg.content,
                      files: msg.files || null,
                      tools: msg.tools_used || null,
                      isStreaming: false,
                      metadata: msg.metadata || null,
                    }))
                    console.log(data)
                    setMessages(newMessages)

                    // Backend already saves the conversation automatically
                    // Extract conversation_id from response to use for subsequent messages
                    if (data.conversation && Array.isArray(data.conversation)) {
                      const lastMessage =
                        data.conversation[data.conversation.length - 1]
                      const conversationId =
                        lastMessage?.metadata?.conversation_id
                      if (conversationId && !currentConversationId) {
                        // This is a new conversation, update our state to use this ID for future messages
                        updateCurrentConversationId(conversationId)
                      }
                    }
                    break
                  }
                } catch (e) {
                  // Skip malformed JSON lines - this is expected for incomplete chunks
                  console.warn(
                    'Failed to parse streaming data:',
                    line.substring(0, 100) + '...'
                  )
                }
              }
            }
          }
        }

        // Mark streaming as complete when done
        setMessages((prev) => {
          const newMessages = [...prev]
          const lastMessage = newMessages[newMessages.length - 1]
          if (lastMessage && lastMessage.who === 'AI') {
            lastMessage.isStreaming = false
          }
          return newMessages
        })
      }
    } catch (error) {
      console.error('Error sending message:', error)

      // Fallback error message
      const errorResponse: ChatMessage = {
        who: 'AI',
        message:
          'Desculpe, ocorreu um erro ao processar sua mensagem. Tente novamente.',
        metadata: {
          source: 'error_fallback',
          timestamp: Date.now(),
        },
      }

      setMessages((prev) => [...prev, errorResponse])
    } finally {
      setIsLoading(false)
      // Keep tools selected for subsequent messages
    }
  }

  const handleRetry = async () => {
    if (isLoading || isRetrying || messages.length < 2) return

    // Find the last user message and last AI message
    const lastAiMessageIndex = messages.length - 1
    const lastAiMessage = messages[lastAiMessageIndex]

    // Only retry if the last message is from AI
    if (lastAiMessage.who !== 'AI') return

    // Find the corresponding user message (should be right before the AI message)
    let lastUserMessage = null
    for (let i = lastAiMessageIndex - 1; i >= 0; i--) {
      if (messages[i].who === 'user') {
        lastUserMessage = messages[i]
        break
      }
    }

    if (!lastUserMessage) return

    setIsRetrying(true)

    try {
      // Remove the last AI message from the conversation
      const messagesWithoutLastAi = messages.slice(0, lastAiMessageIndex)
      setMessages(messagesWithoutLastAi)

      // Rebuild the request messages up to the last user message - preserve all message data
      const requestMessages = messagesWithoutLastAi.map((msg) => {
        const message: any = {
          role: msg.who === 'user' ? 'user' : 'assistant',
          content: msg.message,
        }

        // Preserve files if they exist
        if (msg.files && msg.files.length > 0) {
          message.files = msg.files
        }

        // Preserve tools_used if they exist
        if (msg.tools) {
          message.tools_used = msg.tools
        }

        // Preserve metadata if it exists
        if (msg.metadata) {
          message.metadata = msg.metadata
        }

        return message
      })

      // Re-send the request with the same tools that were used originally
      const toolsToUse = lastUserMessage.tools || {
        generete_chart: false,
        analisar_transcripts: false,
        sell_sides: false,
      }

      // Add empty AI message that will be filled with streaming content
      const streamingMessage: ChatMessage = {
        who: 'AI',
        message: '',
        isStreaming: true,
      }
      setMessages((prev) => [...prev, streamingMessage])

      // Get token from the existing auth system
      const tokenResponse = await fetch('/api/auth/token', {
        credentials: 'include',
      })

      if (!tokenResponse.ok) {
        throw new Error('Failed to get authentication token')
      }

      const { token } = await tokenResponse.json()

      // Choose endpoint based on tools used in the retry
      const endpoint =
        toolsToUse.analisar_transcripts || (toolsToUse as any).sell_sides
          ? 'https://api.jgp.com.br/compass_llm/api/v1/chat/stream_transcripts'
          : 'https://api.jgp.com.br/compass_llm/api/v1/chat/stream'

      // Use the token for direct backend call
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          messages: requestMessages,
          tools: toolsToUse,
          user_name: email || 'anonymous',
          conversation_id: currentConversationId,
        }),
      })

      if (!response.ok) {
        throw new Error('Failed to retry message')
      }

      // Handle streaming response (same logic as handleSendMessage)
      const reader = response.body?.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      if (reader) {
        while (true) {
          const { done, value } = await reader.read()

          if (done) break

          const chunk = decoder.decode(value, { stream: true })
          buffer += chunk

          const messages = buffer.split('\n\n')
          buffer = messages.pop() || ''

          for (const message of messages) {
            const lines = message.split('\n')

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const jsonStr = line.slice(6).trim()
                  if (!jsonStr) continue

                  const data = JSON.parse(jsonStr)

                  // Handle text chunks
                  if (data.type === 'text_chunk' && data.accumulated) {
                    setMessages((prev) => {
                      const newMessages = [...prev]
                      const lastMessage = newMessages[newMessages.length - 1]
                      if (lastMessage && lastMessage.who === 'AI') {
                        lastMessage.message = data.accumulated
                      }
                      return newMessages
                    })
                  }

                  // Handle completion
                  if (
                    data.type === 'completion' &&
                    data.conversation &&
                    Array.isArray(data.conversation)
                  ) {
                    const newMessages = data.conversation.map((msg: any) => ({
                      who: msg.role === 'user' ? 'user' : 'AI',
                      message: msg.content,
                      files: msg.files || null,
                      tools: msg.tools_used || null,
                      isStreaming: false,
                      metadata: msg.metadata || null,
                    }))
                    setMessages(newMessages)

                    // Extract conversation_id from response if needed
                    if (data.conversation && Array.isArray(data.conversation)) {
                      const lastMessage =
                        data.conversation[data.conversation.length - 1]
                      const conversationId =
                        lastMessage?.metadata?.conversation_id
                      if (conversationId && !currentConversationId) {
                        updateCurrentConversationId(conversationId)
                      }
                    }
                    break
                  }
                } catch (e) {
                  console.warn(
                    'Failed to parse streaming data:',
                    line.substring(0, 100) + '...'
                  )
                }
              }
            }
          }
        }

        // Mark streaming as complete
        setMessages((prev) => {
          const newMessages = [...prev]
          const lastMessage = newMessages[newMessages.length - 1]
          if (lastMessage && lastMessage.who === 'AI') {
            lastMessage.isStreaming = false
          }
          return newMessages
        })
      }
    } catch (error) {
      console.error('Error retrying message:', error)

      // Add error message
      const errorResponse: ChatMessage = {
        who: 'AI',
        message:
          'Desculpe, ocorreu um erro ao tentar novamente. Tente mais uma vez.',
        metadata: {
          source: 'retry_error',
          timestamp: Date.now(),
        },
      }

      setMessages((prev) => [...prev, errorResponse])
    } finally {
      setIsRetrying(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  return (
    <div className={classNames('flex h-full flex-col bg-white', className)}>
      {/* Conversation Manager */}
      <div className="border-b border-gray-200 p-4">
        {conversationsLoading ? (
          <div className="flex items-center space-x-2">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-info-dark"></div>
            <span className="text-sm text-gray-600">
              Carregando conversas...
            </span>
          </div>
        ) : (
          <ConversationManager
            conversations={conversations}
            currentConversationId={currentConversationId}
            onSelectConversation={loadConversation}
            onRenameConversation={renameConversation}
            onDeleteConversation={deleteConversation}
            onDeleteAllConversations={deleteAllConversations}
            onCreateNewConversation={createNewConversation}
          />
        )}

        {/* Error Display */}
        {conversationsError && (
          <div className="mt-2 rounded-md bg-red-50 p-3">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">{conversationsError}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Messages area */}
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        {messages.map((message, index) => {
          const isLastMessage = index === messages.length - 1
          const isLastAiMessage =
            isLastMessage && message.who === 'AI' && !message.isStreaming

          return (
            <MessageBubble
              key={index}
              message={message}
              onRetry={isLastAiMessage ? handleRetry : undefined}
              isLastAiMessage={isLastAiMessage}
              isRetrying={isRetrying}
              onImageClick={handleImageClick}
            />
          )
        })}

        {(isLoading || isRetrying) && (
          <div className="flex justify-start">
            <div className="max-w-xs rounded-lg border border-gray-200 bg-gray-100 px-4 py-2 text-gray-800 lg:max-w-md">
              <div className="flex items-center space-x-1">
                <div className="flex space-x-1">
                  <div className="h-2 w-2 animate-bounce rounded-full bg-gray-400"></div>
                  <div
                    className="h-2 w-2 animate-bounce rounded-full bg-gray-400"
                    style={{ animationDelay: '0.1s' }}
                  ></div>
                  <div
                    className="h-2 w-2 animate-bounce rounded-full bg-gray-400"
                    style={{ animationDelay: '0.2s' }}
                  ></div>
                </div>
                <span className="text-sm text-gray-600">
                  {isRetrying
                    ? 'AI está tentando novamente...'
                    : 'AI está digitando...'}
                </span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div className="border-t border-gray-200 p-4">
        <div className="mb-3 flex items-center justify-between">
          <ToolsSelector tools={selectedTools} onChange={setSelectedTools} />
        </div>

        <div className="flex items-end space-x-2">
          <div className="flex-1">
            <ChatInput
              value={currentMessage}
              onChange={setCurrentMessage}
              onKeyPress={handleKeyPress}
              placeholder="Digite sua mensagem..."
              className="w-full resize-none rounded-lg border border-gray-300 py-3 px-4 focus:border-transparent focus:ring-2 focus:ring-info-dark"
              disabled={isLoading || isRetrying}
            />
          </div>

          <Button
            color="primary"
            onClick={handleSendMessage}
            disabled={!currentMessage.trim() || isLoading || isRetrying}
            loading={isLoading || isRetrying}
            className="rounded-lg px-6 py-3"
          >
            Enviar
          </Button>
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4">
          <div className="relative max-h-full max-w-full">
            <button
              onClick={handleCloseImageModal}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 focus:outline-none"
              title="Fechar"
            >
              <svg
                className="h-8 w-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <img
              src={selectedImage.src}
              alt={selectedImage.alt || 'Image'}
              className="max-h-[90vh] max-w-[90vw] rounded-lg shadow-2xl"
              onClick={handleCloseImageModal}
            />
            {selectedImage.alt && (
              <div className="mt-2 text-center text-sm text-white">
                {selectedImage.alt}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default Chat
